import { useEffect, useRef, useState, Suspense, lazy } from "react";

const Globe = lazy(() => import("react-globe.gl"));

import { GLOBE_ROUTES } from "@/data/staticContent";

export default function Hero3DGlobe() {
  const globeRef = useRef();
  const wrapperRef = useRef(null);
  const [size, setSize] = useState({ w: 520, h: 520 });

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        const s = Math.min(width, 620);
        setSize({ w: s, h: s });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (globeRef.current) {
      const controls = globeRef.current.controls();
      if (controls) {
        controls.autoRotate = true;
        controls.autoRotateSpeed = 0.6;
        controls.enableZoom = false;
      }
      globeRef.current.pointOfView({ lat: 20, lng: 60, altitude: 2.4 }, 0);
    }
  }, [size]);

  const arcsData = GLOBE_ROUTES.map((r) => ({
    startLat: r.start[0], startLng: r.start[1],
    endLat: r.end[0], endLng: r.end[1],
    color: r.color,
  }));

  const pointsData = GLOBE_ROUTES.flatMap((r) => [
    { lat: r.start[0], lng: r.start[1], color: r.color, size: 0.55 },
    { lat: r.end[0], lng: r.end[1], color: r.color, size: 0.75 },
  ]);

  return (
    <div ref={wrapperRef} className="w-full aspect-square max-w-[620px] mx-auto relative" data-testid="hero-globe-wrapper">
      <Suspense fallback={<div className="w-full h-full grid place-items-center text-slate-400">Loading globe…</div>}>
        <Globe
          ref={globeRef}
          width={size.w}
          height={size.h}
          backgroundColor="rgba(0,0,0,0)"
          showAtmosphere={true}
          atmosphereColor="#60A5FA"
          atmosphereAltitude={0.22}
          globeImageUrl="//unpkg.com/three-globe/example/img/earth-day.jpg"
          arcsData={arcsData}
          arcColor={(d) => [`${d.color}00`, d.color, `${d.color}00`]}
          arcDashLength={0.35}
          arcDashGap={0.9}
          arcDashAnimateTime={2600}
          arcStroke={0.55}
          arcAltitudeAutoScale={0.6}
          pointsData={pointsData}
          pointColor={(d) => d.color}
          pointAltitude={0.02}
          pointRadius={(d) => d.size}
        />
      </Suspense>
    </div>
  );
}
